/**
 * 4-factor HIPAA risk assessment (45 CFR 164.402).
 * Probability formulas; guided factor questions.
 */

import React, { useState, useEffect } from 'react';
import PrivacyTooltip from './PrivacyTooltip';
import { exportRiskAssessmentToWord } from './exportPrivacyWord';

const API = process.env.REACT_APP_API_URL || '';

// Likelihood and Impact with guidance (45 CFR 164.402)
const LIKELIHOOD_OPTIONS = [
  { value: 'low', label: 'Low', guidance: 'Unlikely to result in compromise. E.g. recipient has confidentiality obligations (BA, CE, workforce); material returned unopened; limited data set; no evidence of access.' },
  { value: 'medium', label: 'Medium', guidance: 'Some chance of compromise. E.g. unclear if accessed; recipient may have limited obligations; partial retrieval.' },
  { value: 'high', label: 'High', guidance: 'Likely to result in compromise. E.g. disclosed to general public, media, or unknown party; stolen/lost device; full identifiers; evidence of access.' }
];
const IMPACT_OPTIONS = [
  { value: 'minimal', label: 'Minimal', guidance: 'Limited identifiers, low sensitivity. E.g. name only; limited data set; no clinical notes, SSN, or financial data.' },
  { value: 'moderate', label: 'Moderate', guidance: 'More identifiers or sensitive data. E.g. name + DOB; clinical info; moderate volume; some sensitive diagnoses.' },
  { value: 'severe', label: 'Severe', guidance: 'Full identifiers, highly sensitive. E.g. SSN, financial data; HIV, substance use, mental health, STD; large volume; full clinical records.' }
];

// Scoring matrix: Likelihood × Impact → factor score. Total = sum of 4 factors. >10 = breach.
const SCORING_MATRIX = {
  low: { minimal: 0.1, moderate: 1, severe: 5 },
  medium: { minimal: 0.5, moderate: 5, severe: 25 },
  high: { minimal: 1, moderate: 10, severe: 50 }
};

function getFactorScore(likelihood, impact) {
  if (!likelihood || !impact) return null;
  const row = SCORING_MATRIX[likelihood];
  return row ? row[impact] ?? null : null;
}

// Guided questions for each factor (45 CFR 164.402)
const FACTOR_QUESTIONS = {
  factor1: {
    title: '1. The nature and extent of the PHI involved',
    cfr: '45 CFR §164.402(2)(i)',
    questions: [
      'What types of identifiers were involved? (e.g. name, MRN, address, DOB, SSN, date of service)',
      'Was sensitive diagnosis information involved? (HIV, substance use, mental health, STD, medications indicating sensitive diagnosis)',
      'What is the likelihood of re-identification? (limited data set vs. full identifiers)',
      'What was the volume of PHI exposed?'
    ],
    tooltip: 'Consider sensitivity, volume, and re-identification risk. Highly sensitive combinations raise probability of compromise.'
  },
  factor2: {
    title: '2. The unauthorized person who used the PHI or to whom disclosure was made',
    cfr: '45 CFR §164.402(2)(ii)',
    questions: [
      'Does the recipient have confidentiality obligations? (business associate, covered entity, internal workforce)',
      'Or was it a non-covered entity, media, member of the public, patient\'s employer, or unknown/lost/stolen?',
      'Could the recipient re-identify individuals from other information available to them?'
    ],
    tooltip: 'A recipient bound by HIPAA obligations presents less risk than the general public or media.'
  },
  factor3: {
    title: '3. Whether the PHI was actually acquired or viewed',
    cfr: '45 CFR §164.402(2)(iii)',
    questions: [
      'Was the PHI actually accessed, viewed, or retained? (e.g. letter returned unopened vs. opened and read)',
      'Was it verbal disclosure only, view-only, or full acquisition?',
      'Was it paper/fax or electronic? Can audit logs or bounce messages confirm non-access?'
    ],
    tooltip: 'If the information was never actually viewed or taken, risk is often lower.'
  },
  factor4: {
    title: '4. The extent to which the risk to the PHI has been mitigated',
    cfr: '45 CFR §164.402(2)(iv)',
    questions: [
      'Did we get the information back? Was it returned complete?',
      'Was it properly destroyed with attestation? How reliable is the attestation?',
      'Did we obtain reliable assurances that the PHI will not be further used or disclosed?',
      'What controls are in place? (encryption, remote wipe, password protection)'
    ],
    tooltip: 'Rapid containment, attestations, and retrieval reduce probability of compromise.'
  }
};
const DETERMINATION_OPTIONS = [
  { value: 'not_violation', label: 'Not a privacy violation' },
  { value: 'violation_not_breach', label: 'Violation but low risk — not a reportable breach' },
  { value: 'breach_requiring_notification', label: 'Breach — we must notify individuals (and HHS if 500+)' },
  { value: 'pending', label: 'Pending (not yet decided)' }
];
const EXCEPTION_OPTIONS = [
  { value: 'none', label: 'No exception — complete the four questions below' },
  { value: 'good_faith_workforce', label: 'Accidental access by our own staff, in good faith' },
  { value: 'inadvertent_authorized', label: 'Accidental disclosure to someone else who was allowed to have it (same org or business associate)' },
  { value: 'recipient_could_not_retain', label: 'Recipient could not reasonably keep the information' }
];

// Short, colour-coded label for a determination — used on the version timeline.
function determinationChip(d) {
  const map = {
    breach_requiring_notification: { label: 'Breach, notify', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
    violation_not_breach: { label: 'Low risk, not a breach', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    not_violation: { label: 'Not a violation', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    pending: { label: 'Pending', cls: 'text-slate-600 bg-slate-50 border-slate-200' },
  };
  return map[d] || { label: d || 'Pending', cls: 'text-slate-600 bg-slate-50 border-slate-200' };
}

function versionDate(ra) {
  const raw = ra?.completed_at || ra?.created_at;
  if (!raw) return '';
  try { return new Date(raw).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(raw).slice(0, 10); }
}

export default function RiskAssessmentForm({ incident, incidentId, onSave, canEdit }) {
  const current = incident?.current_risk_assessment || incident?.risk_assessments?.[0];
  const versions = incident?.risk_assessments || [];
  // The officer can scroll back through earlier dated versions (read-only); the
  // current version stays the one you edit and save.
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const active = (selectedVersionId && versions.find((v) => v.id === selectedVersionId)) || current;
  const viewingHistory = !!(active && current && active.id !== current.id);
  const [form, setForm] = useState(initialForm(active));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [createError, setCreateError] = useState('');
  const locked = current?.determination_locked === true;
  const editable = canEdit && !locked && !viewingHistory;
  // The agent stays available after approval (only a closed case blocks it):
  // the 164.404(c)(1)(B) identifier and scope work continues on the 60-day
  // clock. A run on a locked case writes a new dated addendum version; the
  // approved version stays locked as the determination of record.
  const caseApprovedBreach = incident?.status === 'approved_breach';
  const postDetermination = locked && canEdit;

  useEffect(() => {
    setForm(initialForm(active));
  }, [active?.id]);

  // Auto-populate factor score when Likelihood and Impact selected
  useEffect(() => {
    if (form.exception_applied !== 'none' || !editable) return;
    const factors = [
      { l: 'factor1_likelihood', i: 'factor1_impact', s: 'factor1_score' },
      { l: 'factor2_likelihood', i: 'factor2_impact', s: 'factor2_score' },
      { l: 'factor3_likelihood', i: 'factor3_impact', s: 'factor3_score' },
      { l: 'factor4_likelihood', i: 'factor4_impact', s: 'factor4_score' }
    ];
    let changed = false;
    const next = { ...form };
    for (const { l, i, s } of factors) {
      const score = getFactorScore(form[l], form[i]);
      if (score != null && String(form[s]) !== String(score)) {
        next[s] = String(score);
        changed = true;
      }
    }
    if (changed) setForm(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form read for L/I; adding form would cause loops
  }, [form.exception_applied, editable, form.factor1_likelihood, form.factor1_impact, form.factor2_likelihood, form.factor2_impact, form.factor3_likelihood, form.factor3_impact, form.factor4_likelihood, form.factor4_impact]);

  function initialForm(ra) {
    const emptyResponses = (keys) => Object.fromEntries(keys.map((_, i) => [String(i), '']));
    const fromResponses = (obj) => (obj && typeof obj === 'object' ? obj : {});
    if (!ra) {
      return {
        factor1_nature_extent_phi: '',
        factor1_sensitive_diagnoses: [],
        factor1_responses: emptyResponses([0, 1, 2, 3]),
        factor1_likelihood: '',
        factor1_impact: '',
        factor1_score: '',
        factor1_notes: '',
        factor2_unauthorized_person: '',
        factor2_responses: emptyResponses([0, 1, 2]),
        factor2_likelihood: '',
        factor2_impact: '',
        factor2_score: '',
        factor2_notes: '',
        factor3_actually_acquired_viewed: null,
        factor3_responses: emptyResponses([0, 1, 2]),
        factor3_likelihood: '',
        factor3_impact: '',
        factor3_score: '',
        factor3_notes: '',
        factor4_extent_mitigation: '',
        factor4_responses: emptyResponses([0, 1, 2, 3]),
        factor4_likelihood: '',
        factor4_impact: '',
        factor4_score: '',
        factor4_notes: '',
        combined_score: '',
        likelihood_overall: '',
        impact_overall: '',
        exception_applied: 'none',
        exception_notes: '',
        determination: 'pending',
        narrative_summary: ''
      };
    }
    const r1 = fromResponses(ra.factor1_responses);
    const r2 = fromResponses(ra.factor2_responses);
    const r3 = fromResponses(ra.factor3_responses);
    const r4 = fromResponses(ra.factor4_responses);
    return {
      factor1_nature_extent_phi: ra.factor1_nature_extent_phi || '',
      factor1_sensitive_diagnoses: Array.isArray(ra.factor1_sensitive_diagnoses) ? ra.factor1_sensitive_diagnoses : [],
      factor1_responses: { '0': r1[0] ?? '', '1': r1[1] ?? '', '2': r1[2] ?? '', '3': r1[3] ?? '' },
      factor1_likelihood: ra.factor1_likelihood || '',
      factor1_impact: ra.factor1_impact || '',
      factor1_score: ra.factor1_score ?? '',
      factor1_notes: ra.factor1_notes || '',
      factor2_unauthorized_person: ra.factor2_unauthorized_person || '',
      factor2_responses: { '0': r2[0] ?? '', '1': r2[1] ?? '', '2': r2[2] ?? '' },
      factor2_likelihood: ra.factor2_likelihood || '',
      factor2_impact: ra.factor2_impact || '',
      factor2_score: ra.factor2_score ?? '',
      factor2_notes: ra.factor2_notes || '',
      factor3_actually_acquired_viewed: ra.factor3_actually_acquired_viewed,
      factor3_responses: { '0': r3[0] ?? '', '1': r3[1] ?? '', '2': r3[2] ?? '' },
      factor3_likelihood: ra.factor3_likelihood || '',
      factor3_impact: ra.factor3_impact || '',
      factor3_score: ra.factor3_score ?? '',
      factor3_notes: ra.factor3_notes || '',
      factor4_extent_mitigation: ra.factor4_extent_mitigation || '',
      factor4_responses: { '0': r4[0] ?? '', '1': r4[1] ?? '', '2': r4[2] ?? '', '3': r4[3] ?? '' },
      factor4_likelihood: ra.factor4_likelihood || '',
      factor4_impact: ra.factor4_impact || '',
      factor4_score: ra.factor4_score ?? '',
      factor4_notes: ra.factor4_notes || '',
      combined_score: ra.combined_score ?? '',
      likelihood_overall: ra.likelihood_overall || '',
      impact_overall: ra.impact_overall || '',
      exception_applied: ra.exception_applied || 'none',
      exception_notes: ra.exception_notes || '',
      determination: ra.determination || 'pending',
      narrative_summary: ra.narrative_summary || '',
      officer_position_response: ra.officer_position_response || '',
      verification_response: ra.verification_response || ''
    };
  }

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };
  const updateResponse = (factorKey, qIndex, value) => {
    setForm((f) => ({ ...f, [factorKey]: { ...(f[factorKey] || {}), [String(qIndex)]: value } }));
  };

  // "Suggest with agent" → the grounded HIPAA agent writes its four-factor read
  // straight into a NEW dated, editable version (no modal). Each click creates a
  // new version, so the assessment builds a timeline as information arrives.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  // A run can be started by anyone or anything (this button, the specialist's
  // request_reassessment, the API). On load, ask the server whether one is in
  // flight RIGHT NOW and, if so, show it and watch it to completion, so a
  // running assessment is never invisible to whoever opens the tab.
  const [externalRun, setExternalRun] = useState(false);
  useEffect(() => {
    let on = true;
    const mountedAt = new Date().toISOString();
    const check = async () => {
      try {
        const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/assess/running`, { credentials: 'include' });
        const d = await r.json().catch(() => ({}));
        if (!on) return;
        if (d.running) {
          setExternalRun(true);
          // Watch for the version to land, then refresh the tab.
          const poll = async () => {
            while (on) {
              await new Promise((res) => setTimeout(res, 5000));
              try {
                const s = await fetch(`${API}/api/privacy/incidents/${incidentId}/assess/suggest-status?started_at=${encodeURIComponent(mountedAt)}`, { credentials: 'include' });
                const sd = await s.json().catch(() => ({}));
                if (sd.state === 'done') { if (on) { setExternalRun(false); setSelectedVersionId(null); onSave(); } return; }
                const rr = await fetch(`${API}/api/privacy/incidents/${incidentId}/assess/running`, { credentials: 'include' });
                const rd = await rr.json().catch(() => ({}));
                if (!rd.running) { if (on) setExternalRun(false); return; }
              } catch { /* keep watching */ }
            }
          };
          poll();
        }
      } catch { /* no indicator is better than a wrong one */ }
    };
    check();
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  // Evidence freshness: flag when documents or updates were added AFTER the
  // current assessment was produced, and when uploaded files have no readable
  // text (the agent cannot see those). Cheap reads of endpoints that exist.
  const [evidence, setEvidence] = useState({ docs: [], updates: [] });
  useEffect(() => {
    let on = true;
    Promise.all([
      fetch(`${API}/api/privacy/incidents/${incidentId}/documents`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({})),
      fetch(`${API}/api/privacy/incidents/${incidentId}/updates`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({})),
    ]).then(([d, u]) => { if (on) setEvidence({ docs: d.documents || [], updates: u.updates || [] }); });
    return () => { on = false; };
  }, [incidentId, current?.id]);
  const assessedAt = current ? new Date(current.completed_at || current.created_at || 0).getTime() : 0;
  const newerDocs = current ? evidence.docs.filter((d) => new Date(d.created_at).getTime() > assessedAt) : [];
  const newerUpdates = current ? evidence.updates.filter((u) => new Date(u.created_at || u.occurred_at).getTime() > assessedAt) : [];
  const unreadableDocs = evidence.docs.filter((d) => d.has_file && !d.has_text);
  const evidenceStale = !!current && (newerDocs.length > 0 || newerUpdates.length > 0);
  const runningBanner = externalRun && (
    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
      <span className="font-semibold">The privacy agent is assessing this case right now.</span>{' '}
      The new dated version will appear here the moment it completes; you can keep working elsewhere in the case.
    </div>
  );

  const staleBanner = (evidenceStale || unreadableDocs.length > 0) && (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-1">
      {evidenceStale && (
        <p>
          <span className="font-semibold">Evidence has moved since this assessment.</span>{' '}
          {newerDocs.length > 0 && `${newerDocs.length} document(s)`}
          {newerDocs.length > 0 && newerUpdates.length > 0 && ' and '}
          {newerUpdates.length > 0 && `${newerUpdates.length} case update(s)`}
          {' '}were added after version {current?.version_number} was produced. Click Suggest with agent to bring the determination current.
        </p>
      )}
      {unreadableDocs.length > 0 && (
        <p>
          <span className="font-semibold">{unreadableDocs.length} uploaded document(s) have no readable text</span> and are invisible to the agent: {unreadableDocs.map((d) => d.file_name).join(', ')}. Re-upload them as a text PDF, Word document, or spreadsheet so they count as evidence.
        </p>
      )}
    </div>
  );

  // Reviewer note to the assessor. The note is TAGGED with the part of the
  // assessment it concerns, so the agent knows whether it is a scoring point, a
  // factor call, the determination, or the notification plan, and applies it there.
  const FEEDBACK_ABOUT = [
    { value: 'determination', label: 'Overall determination / probability of compromise' },
    { value: 'factor1', label: 'Factor 1 — nature and extent of the PHI' },
    { value: 'factor2', label: 'Factor 2 — the unauthorized recipient' },
    { value: 'factor3', label: 'Factor 3 — whether acquired or viewed' },
    { value: 'factor4', label: 'Factor 4 — mitigation' },
    { value: 'scoring', label: 'Likelihood / impact scoring' },
    { value: 'notification', label: 'Notification plan and next steps' },
    { value: 'general', label: 'General approach' },
  ];
  const [feedbackAbout, setFeedbackAbout] = useState('determination');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const sendFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackBusy(true);
    setFeedbackMsg('');
    try {
      const aboutLabel = (FEEDBACK_ABOUT.find((o) => o.value === feedbackAbout) || {}).label || 'General';
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/ai/feedback`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent: 'privacy_assessor', field: 'assessment_feedback', corrected_value: `[Applies to: ${aboutLabel}] ${feedbackText.trim()}`, reason: `Reviewer note re: ${aboutLabel}` }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFeedbackText('');
        setFeedbackMsg('Saved. The agent will apply this the next time you click Suggest with agent.');
      } else {
        setFeedbackMsg(data.message || 'Could not save the feedback. Please try again.');
      }
    } catch (e) {
      setFeedbackMsg(e.message || 'Could not save the feedback. Please try again.');
    } finally {
      setFeedbackBusy(false);
    }
  };
  const suggestAiVersion = async () => {
    setAiError('');
    setAiBusy(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/assess/suggest-version`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setAiError(data.message || 'The privacy agent could not complete the assessment.');
        return;
      }
      if (data.risk_assessment) {
        // Synchronous completion (older server): the version is already written.
        setSelectedVersionId(null);
        onSave();
        return;
      }
      // The run now happens in the background so a long investigation survives
      // the gateway's request timeout. Poll until the new version lands or the
      // run reports an error.
      const startedAt = data.started_at;
      const deadline = Date.now() + 20 * 60 * 1000;
      for (;;) {
        await new Promise((r) => setTimeout(r, 5000));
        let sd = {};
        try {
          const s = await fetch(`${API}/api/privacy/incidents/${incidentId}/assess/suggest-status?started_at=${encodeURIComponent(startedAt)}`, { credentials: 'include' });
          sd = await s.json().catch(() => ({}));
        } catch { sd = {}; }
        if (sd.state === 'done') {
          setSelectedVersionId(null); // jump to the freshly created current version
          onSave(); // refresh → the new dated version is current + editable below
          return;
        }
        if (sd.state === 'error') {
          setAiError(sd.message || 'The privacy agent could not complete the assessment.');
          return;
        }
        if (Date.now() > deadline) {
          setAiError('The agent is still working on this case. Come back in a few minutes and refresh; the new assessment version will appear when it finishes.');
          return;
        }
      }
    } catch (e) {
      setAiError(e.message || 'The privacy agent is unavailable.');
    } finally {
      setAiBusy(false);
    }
  };

  // Combined score = sum of 4 factor scores. Total > 10 = breach.
  const factorScores = [
    form.factor1_score === '' || form.factor1_score == null ? null : parseFloat(form.factor1_score),
    form.factor2_score === '' || form.factor2_score == null ? null : parseFloat(form.factor2_score),
    form.factor3_score === '' || form.factor3_score == null ? null : parseFloat(form.factor3_score),
    form.factor4_score === '' || form.factor4_score == null ? null : parseFloat(form.factor4_score)
  ].filter((n) => typeof n === 'number' && !Number.isNaN(n));
  const suggestedCombined = factorScores.length === 4
    ? Math.round((factorScores.reduce((a, b) => a + b, 0)) * 100) / 100
    : null;
  const canSuggest = factorScores.length === 4;
  const applySuggested = () => {
    if (suggestedCombined != null) update('combined_score', String(suggestedCombined));
  };
  const totalScore = suggestedCombined ?? (form.combined_score !== '' && form.combined_score != null ? parseFloat(form.combined_score) : null);
  const breachSuggested = totalScore != null ? totalScore > 10 : null;

  const saveCurrent = async () => {
    if (!current?.id || locked) return;
    setSaveError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/risk-assessments/${current.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          factor1_score: form.factor1_score === '' ? null : parseFloat(form.factor1_score),
          factor2_score: form.factor2_score === '' ? null : parseFloat(form.factor2_score),
          factor3_score: form.factor3_score === '' ? null : parseFloat(form.factor3_score),
          factor4_score: form.factor4_score === '' ? null : parseFloat(form.factor4_score),
          combined_score: form.combined_score === '' ? null : parseFloat(form.combined_score),
          factor1_responses: form.factor1_responses,
          factor2_responses: form.factor2_responses,
          factor3_responses: form.factor3_responses,
          factor4_responses: form.factor4_responses
        })
      });
      const data = res.ok ? await res.json() : await res.json().catch(() => ({}));
      if (res.ok) {
        onSave();
      } else {
        setSaveError(data.message || res.statusText || 'Could not save. Please try again.');
      }
    } catch (e) {
      setSaveError(e.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const createNewVersion = async () => {
    setCreateError('');
    setCreatingNew(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/risk-assessments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          factor1_sensitive_diagnoses: form.factor1_sensitive_diagnoses,
          factor1_score: form.factor1_score === '' ? null : parseFloat(form.factor1_score),
          factor2_score: form.factor2_score === '' ? null : parseFloat(form.factor2_score),
          factor3_score: form.factor3_score === '' ? null : parseFloat(form.factor3_score),
          factor4_score: form.factor4_score === '' ? null : parseFloat(form.factor4_score),
          combined_score: form.combined_score === '' ? null : parseFloat(form.combined_score),
          factor1_responses: form.factor1_responses,
          factor2_responses: form.factor2_responses,
          factor3_responses: form.factor3_responses,
          factor4_responses: form.factor4_responses
        })
      });
      const data = res.ok ? await res.json() : await res.json().catch(() => ({}));
      if (res.ok) {
        setSelectedVersionId(null);
        onSave();
      } else {
        setCreateError(data.message || res.statusText || 'Could not start risk assessment. Please try again.');
      }
    } catch (e) {
      setCreateError(e.message || 'Could not start risk assessment. Please try again.');
    } finally {
      setCreatingNew(false);
    }
  };

  if (!current && !canEdit) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <p className="text-slate-500">No risk assessment yet. Add one when the incident is under investigation.</p>
          <button type="button" onClick={() => exportRiskAssessmentToWord(incident)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors">
            Download as Word
          </button>
        </div>
      </div>
    );
  }

  if (!current && canEdit) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-semibold text-slate-900">Risk assessment</h3>
          <button type="button" onClick={() => exportRiskAssessmentToWord(incident)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors">
            Download as Word
          </button>
        </div>
        <p className="text-slate-600 text-sm mb-4">
          Answer the four risk questions below to decide whether this incident is a breach that must be reported — or let the privacy agent draft a first pass for you to review. If an exception applies, you can note it instead.
        </p>
        {(createError || aiError) && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{createError || aiError}</div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={suggestAiVersion}
            disabled={aiBusy}
            title="The privacy agent reads the incident, grounds its analysis in the controlling HIPAA text, and drafts a dated four-factor assessment for you to review and edit."
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {aiBusy && <span className="inline-block h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {aiBusy ? 'Assessing…' : 'Draft with agent'}
          </button>
          <button
            type="button"
            onClick={createNewVersion}
            disabled={creatingNew}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
          >
            {creatingNew ? 'Creating...' : 'Start blank assessment'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            Risk assessment {active?.version_number != null ? `(v${active.version_number})` : ''}
            {active?.generated_by_ai && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">Agent-drafted</span>
            )}
            {locked && <span className="text-sm font-normal text-amber-600">Locked after approval</span>}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Four-factor analysis under 45 CFR §164.402. The privacy agent grounds its read in the controlling HIPAA text; you review, edit, and decide.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={suggestAiVersion}
              disabled={aiBusy}
              title={locked
                ? 'The approved version stays locked as the determination of record. The agent writes a new dated addendum version — useful for pinning down the identifiers and scope the notification letter must describe. It cannot walk the approved determination back.'
                : 'The privacy agent reads the incident, grounds its analysis in the controlling HIPAA text, and writes a new dated four-factor draft below for you to review and edit. Each click creates a new dated version.'}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {aiBusy && <span className="inline-block h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {aiBusy ? 'Assessing…' : 'Suggest with agent'}
            </button>
          )}
          <button type="button" onClick={() => exportRiskAssessmentToWord(incident)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors">
            Download as Word
          </button>
        </div>
      </div>

      {aiError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{aiError}</div>
      )}

      {runningBanner}
      {staleBanner}

      {/* Post-determination: the approved version is frozen as the record, but
          the factual work stays open until the case closes. */}
      {postDetermination && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <span className="font-semibold">This version is locked as the approved determination of record.</span>{' '}
          The assessment work stays open until the case is closed: running the agent adds a new dated addendum version, useful for pinning down the identifiers and scope the notification must describe (45 CFR §164.404(c)(1)(B)).
          {caseApprovedBreach && ' The approved breach determination cannot be walked back here; superseding it requires returning the case to determination review and re-approving.'}
        </div>
      )}

      {/* Dated version timeline, each Suggest with agent or New version adds an entry. */}
      {versions.length > 1 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-medium text-slate-500 mb-2">History — {versions.length} versions</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {versions.map((v) => {
              const chip = determinationChip(v.determination);
              const isActive = active?.id === v.id;
              const isCurrent = current?.id === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setSelectedVersionId(isCurrent ? null : v.id)}
                  className={`shrink-0 text-left rounded-xl border px-3 py-2 transition-colors ${isActive ? 'border-slate-900 bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-slate-800">v{v.version_number}</span>
                    {v.generated_by_ai && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">Agent</span>}
                    {isCurrent && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Current</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{versionDate(v)}</div>
                  <div className={`mt-1 inline-block text-[11px] px-1.5 py-0.5 rounded border ${chip.cls}`}>{chip.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {viewingHistory && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-center justify-between gap-3 flex-wrap">
          <span>You're viewing an earlier version (v{active?.version_number}, {versionDate(active)}). It's read-only.</span>
          <button type="button" onClick={() => setSelectedVersionId(null)} className="px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-blue-700 font-medium hover:bg-blue-100">
            Back to current (v{current?.version_number})
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-600 mb-1">
            Does an exception apply so this might not be a breach?
            <PrivacyTooltip text="In some cases (e.g. accidental disclosure to another authorized person), the incident may not count as a breach. If none apply, complete the four questions below." cfr="45 CFR §164.402" />
          </label>
          <select
            value={form.exception_applied}
            onChange={(e) => update('exception_applied', e.target.value)}
            disabled={!editable}
            className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          >
            {EXCEPTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {form.exception_applied !== 'none' && (
            <input
              value={form.exception_notes}
              onChange={(e) => update('exception_notes', e.target.value)}
              placeholder="Brief explanation of why this exception applies"
              disabled={!editable}
              className="mt-2 w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
            />
          )}
        </div>

        {form.exception_applied !== 'none' && (
          <div className="md:col-span-2 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-slate-700 text-sm">
              <strong>Because an exception applies, this incident is not considered a breach.</strong> The four-factor risk analysis and scoring below are not required. Document your conclusion in the summary and determination below, then save.
            </p>
          </div>
        )}

        {form.exception_applied === 'none' && (
          <>
        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 mb-4">
            <h4 className="font-medium text-blue-900 mb-2">How to grade Likelihood and Impact</h4>
            <p className="text-sm text-blue-800 mb-2">
              For each factor, choose <strong>Likelihood</strong> (probability of compromise) and <strong>Impact</strong> (potential harm if compromised). The score is calculated automatically from the matrix below.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-blue-900 mb-1">Likelihood</p>
                <ul className="list-disc list-inside text-blue-800 space-y-0.5">
                  <li><strong>Low:</strong> Recipient has confidentiality obligations; returned unopened; no evidence of access</li>
                  <li><strong>Medium:</strong> Unclear if accessed; recipient may have limited obligations</li>
                  <li><strong>High:</strong> General public, media, stolen device; evidence of access</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-blue-900 mb-1">Impact</p>
                <ul className="list-disc list-inside text-blue-800 space-y-0.5">
                  <li><strong>Minimal:</strong> Limited identifiers; no SSN, financial, or sensitive diagnoses</li>
                  <li><strong>Moderate:</strong> Name + DOB; clinical info; some sensitive data</li>
                  <li><strong>Severe:</strong> SSN, financial; HIV, substance use, mental health; large volume</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              Matrix: Low×Minimal=0.1 … High×Severe=50. Combined = sum of 4 factors. Total &gt; 10 = breach.
            </p>
            <p className="text-xs text-blue-600 mt-1 italic">
              Example: Misdirected email to another covered entity, limited data set, promptly confirmed deletion → Low likelihood, Minimal impact → 0.1 per factor.
            </p>
          </div>
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">{FACTOR_QUESTIONS.factor1.title}</h4>
          <PrivacyTooltip text={FACTOR_QUESTIONS.factor1.tooltip} cfr={FACTOR_QUESTIONS.factor1.cfr} />
          <div className="mt-2 space-y-2">
            {FACTOR_QUESTIONS.factor1.questions.map((q, i) => (
              <div key={i}>
                <label className="block text-sm text-slate-600 mb-0.5">{q}</label>
                <textarea
                  value={form.factor1_responses?.[String(i)] ?? ''}
                  onChange={(e) => updateResponse('factor1_responses', i, e.target.value)}
                  disabled={!editable}
                  rows={2}
                  placeholder="The privacy agent's answer appears here. Edit freely."
                  className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50 whitespace-pre-wrap"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Likelihood</label>
              <PrivacyTooltip text="Low: unlikely (returned unopened, recipient has obligations). Medium: unclear. High: likely (general public, stolen, evidence of access)." cfr="45 CFR §164.402" />
            </div>
            <select value={form.factor1_likelihood} onChange={(e) => update('factor1_likelihood', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={LIKELIHOOD_OPTIONS.find((o) => o.value === form.factor1_likelihood)?.guidance}>
              <option value="">Select…</option>
              {LIKELIHOOD_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Impact</label>
              <PrivacyTooltip text="Minimal: limited identifiers, no SSN/sensitive. Moderate: name+DOB, clinical. Severe: SSN, financial, HIV/substance use, large volume." cfr="45 CFR §164.402" />
            </div>
            <select value={form.factor1_impact} onChange={(e) => update('factor1_impact', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={IMPACT_OPTIONS.find((o) => o.value === form.factor1_impact)?.guidance}>
              <option value="">Select…</option>
              {IMPACT_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Score</label>
              <PrivacyTooltip text="Auto-filled from Likelihood × Impact matrix. Override only if professional judgment differs." cfr="45 CFR §164.402" />
            </div>
            <input type="number" step={0.1} min={0.1} max={100} value={form.factor1_score} onChange={(e) => update('factor1_score', e.target.value)} disabled={!editable} placeholder="Auto" className="w-20 rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" />
          </div>
          <label className="block text-xs font-medium text-slate-500 mt-3 mb-1">Analysis &amp; notes</label>
          <textarea value={form.factor1_notes} onChange={(e) => update('factor1_notes', e.target.value)} disabled={!editable} rows={3} placeholder="The privacy agent fills this with its read of this factor. Edit freely." className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm disabled:bg-slate-50 whitespace-pre-wrap" />
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">{FACTOR_QUESTIONS.factor2.title}</h4>
          <PrivacyTooltip text={FACTOR_QUESTIONS.factor2.tooltip} cfr={FACTOR_QUESTIONS.factor2.cfr} />
          <div className="mt-2 space-y-2">
            {FACTOR_QUESTIONS.factor2.questions.map((q, i) => (
              <div key={i}>
                <label className="block text-sm text-slate-600 mb-0.5">{q}</label>
                <textarea
                  value={form.factor2_responses?.[String(i)] ?? ''}
                  onChange={(e) => updateResponse('factor2_responses', i, e.target.value)}
                  disabled={!editable}
                  rows={2}
                  placeholder="The privacy agent's answer appears here. Edit freely."
                  className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50 whitespace-pre-wrap"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Likelihood</label>
              <PrivacyTooltip text="Low: recipient has obligations. Medium: unclear. High: general public, media, unknown." cfr="45 CFR §164.402(2)(ii)" />
            </div>
            <select value={form.factor2_likelihood} onChange={(e) => update('factor2_likelihood', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={LIKELIHOOD_OPTIONS.find((o) => o.value === form.factor2_likelihood)?.guidance}>
              <option value="">Select…</option>
              {LIKELIHOOD_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Impact</label>
              <PrivacyTooltip text="Based on who received the PHI and whether they could misuse it." cfr="45 CFR §164.402(2)(ii)" />
            </div>
            <select value={form.factor2_impact} onChange={(e) => update('factor2_impact', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={IMPACT_OPTIONS.find((o) => o.value === form.factor2_impact)?.guidance}>
              <option value="">Select…</option>
              {IMPACT_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <label className="text-sm text-slate-600">Score</label>
            <input type="number" step={0.1} min={0.1} max={100} value={form.factor2_score} onChange={(e) => update('factor2_score', e.target.value)} disabled={!editable} placeholder="Auto" className="w-20 rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" />
          </div>
          <label className="block text-xs font-medium text-slate-500 mt-3 mb-1">Analysis &amp; notes</label>
          <textarea value={form.factor2_notes} onChange={(e) => update('factor2_notes', e.target.value)} disabled={!editable} rows={3} placeholder="The privacy agent fills this with its read of this factor. Edit freely." className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm disabled:bg-slate-50 whitespace-pre-wrap" />
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">{FACTOR_QUESTIONS.factor3.title}</h4>
          <PrivacyTooltip text={FACTOR_QUESTIONS.factor3.tooltip} cfr={FACTOR_QUESTIONS.factor3.cfr} />
          <div className="mt-2 flex gap-4 mb-2">
            <label className="flex items-center gap-2">
              <input type="radio" checked={form.factor3_actually_acquired_viewed === true} onChange={() => update('factor3_actually_acquired_viewed', true)} disabled={!editable} />
              <span>Yes — PHI was acquired or viewed</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={form.factor3_actually_acquired_viewed === false} onChange={() => update('factor3_actually_acquired_viewed', false)} disabled={!editable} />
              <span>No — PHI was not accessed (e.g. letter returned unopened)</span>
            </label>
          </div>
          <div className="space-y-2">
            {FACTOR_QUESTIONS.factor3.questions.map((q, i) => (
              <div key={i}>
                <label className="block text-sm text-slate-600 mb-0.5">{q}</label>
                <textarea
                  value={form.factor3_responses?.[String(i)] ?? ''}
                  onChange={(e) => updateResponse('factor3_responses', i, e.target.value)}
                  disabled={!editable}
                  rows={2}
                  placeholder="The privacy agent's answer appears here. Edit freely."
                  className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50 whitespace-pre-wrap"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Likelihood</label>
              <PrivacyTooltip text="Low: returned unopened, audit logs show no access. Medium: unclear. High: opened, viewed, or retained." cfr="45 CFR §164.402(2)(iii)" />
            </div>
            <select value={form.factor3_likelihood} onChange={(e) => update('factor3_likelihood', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={LIKELIHOOD_OPTIONS.find((o) => o.value === form.factor3_likelihood)?.guidance}>
              <option value="">Select…</option>
              {LIKELIHOOD_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Impact</label>
              <PrivacyTooltip text="Based on sensitivity of PHI that was (or may have been) acquired or viewed." cfr="45 CFR §164.402(2)(iii)" />
            </div>
            <select value={form.factor3_impact} onChange={(e) => update('factor3_impact', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={IMPACT_OPTIONS.find((o) => o.value === form.factor3_impact)?.guidance}>
              <option value="">Select…</option>
              {IMPACT_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <label className="text-sm text-slate-600">Score</label>
            <input type="number" step={0.1} min={0.1} max={100} value={form.factor3_score} onChange={(e) => update('factor3_score', e.target.value)} disabled={!editable} placeholder="Auto" className="w-20 rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" />
          </div>
          <label className="block text-xs font-medium text-slate-500 mt-3 mb-1">Analysis &amp; notes</label>
          <textarea value={form.factor3_notes} onChange={(e) => update('factor3_notes', e.target.value)} disabled={!editable} rows={3} placeholder="The privacy agent fills this with its read of this factor (e.g. audit logs, bounce messages). Edit freely." className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm disabled:bg-slate-50 whitespace-pre-wrap" />
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <h4 className="font-medium text-slate-700 mb-1">{FACTOR_QUESTIONS.factor4.title}</h4>
          <PrivacyTooltip text={FACTOR_QUESTIONS.factor4.tooltip} cfr={FACTOR_QUESTIONS.factor4.cfr} />
          <div className="mt-2 space-y-2">
            {FACTOR_QUESTIONS.factor4.questions.map((q, i) => (
              <div key={i}>
                <label className="block text-sm text-slate-600 mb-0.5">{q}</label>
                <textarea
                  value={form.factor4_responses?.[String(i)] ?? ''}
                  onChange={(e) => updateResponse('factor4_responses', i, e.target.value)}
                  disabled={!editable}
                  rows={2}
                  placeholder="The privacy agent's answer appears here. Edit freely."
                  className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50 whitespace-pre-wrap"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Likelihood</label>
              <PrivacyTooltip text="Low: retrieved complete, destroyed with attestation. Medium: partial retrieval. High: not retrieved, no assurances." cfr="45 CFR §164.402(2)(iv)" />
            </div>
            <select value={form.factor4_likelihood} onChange={(e) => update('factor4_likelihood', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={LIKELIHOOD_OPTIONS.find((o) => o.value === form.factor4_likelihood)?.guidance}>
              <option value="">Select…</option>
              {LIKELIHOOD_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-600">Impact</label>
              <PrivacyTooltip text="Based on sensitivity of PHI that remains at risk after mitigation efforts." cfr="45 CFR §164.402(2)(iv)" />
            </div>
            <select value={form.factor4_impact} onChange={(e) => update('factor4_impact', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" title={IMPACT_OPTIONS.find((o) => o.value === form.factor4_impact)?.guidance}>
              <option value="">Select…</option>
              {IMPACT_OPTIONS.map((o) => <option key={o.value} value={o.value} title={o.guidance}>{o.label}</option>)}
            </select>
            <label className="text-sm text-slate-600">Score</label>
            <input type="number" step={0.1} min={0.1} max={100} value={form.factor4_score} onChange={(e) => update('factor4_score', e.target.value)} disabled={!editable} placeholder="Auto" className="w-20 rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" />
          </div>
          <label className="block text-xs font-medium text-slate-500 mt-3 mb-1">Analysis &amp; notes</label>
          <textarea value={form.factor4_notes} onChange={(e) => update('factor4_notes', e.target.value)} disabled={!editable} rows={3} placeholder="The privacy agent fills this with its read of this factor. Edit freely." className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm disabled:bg-slate-50 whitespace-pre-wrap" />
        </div>

        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <label className="block text-sm font-medium text-slate-600 mb-1">
            Overall risk score and likelihood / impact
            <PrivacyTooltip
              text="Scoring matrix: Per-factor score = Likelihood × Impact. Matrix: Low+Minimal=0.1, Low+Moderate=1, Low+Severe=5; Medium+Minimal=0.5, Medium+Moderate=5, Medium+Severe=25; High+Minimal=1, High+Moderate=10, High+Severe=50. Combined score = sum of all 4 factor scores. Total &gt; 10 = breach (notification required); ≤10 = low probability, not a breach."
              cfr="45 CFR §164.402"
            />
          </label>
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <input type="number" step={0.1} min={0} value={form.combined_score} onChange={(e) => update('combined_score', e.target.value)} disabled={!editable} placeholder="Sum of 4 factors" className="w-28 rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" />
              <span className="text-slate-500 text-sm">Combined score (sum)</span>
            </div>
            {suggestedCombined != null && editable && (
              <div className="flex items-center gap-2">
                <span className="text-slate-600 text-sm">
                  {canSuggest ? 'Suggested (sum of all 4 factors):' : `Suggested (sum of ${factorScores.length} factor${factorScores.length === 1 ? '' : 's'} so far):`} <strong>{suggestedCombined}</strong>
                </span>
                <button type="button" onClick={applySuggested} className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 shadow-sm">Use this score</button>
              </div>
            )}
            <select value={form.likelihood_overall} onChange={(e) => update('likelihood_overall', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm">
              <option value="">Likelihood overall</option>
              {LIKELIHOOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={form.impact_overall} onChange={(e) => update('impact_overall', e.target.value)} disabled={!editable} className="rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm">
              <option value="">Impact overall</option>
              {IMPACT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {breachSuggested !== null && (
            <div className={`mt-3 p-3 rounded-xl text-sm font-medium ${breachSuggested ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-green-50 text-green-800 border border-green-200'}`}>
              {breachSuggested
                ? 'Suggested determination: Breach — notification required (total score > 10).'
                : 'Suggested determination: Low probability — not a breach (total score ≤ 10).'}
            </div>
          )}
        </div>
          </>
        )}

        <div className="md:col-span-2 border-t border-slate-200 pt-4">
          <label className="block text-sm font-medium text-slate-600 mb-1">Final determination: Is this a breach we must report?</label>
          <select
            value={form.determination}
            onChange={(e) => update('determination', e.target.value)}
            disabled={!editable}
            className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          >
            {DETERMINATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} disabled={caseApprovedBreach && o.value !== 'breach_requiring_notification'}>{o.label}</option>
            ))}
          </select>
          {caseApprovedBreach && editable && (
            <p className="text-xs text-slate-500 mt-1">
              This case carries an approved breach determination, so later versions cannot move below "Breach — we must notify". Superseding it requires returning the case to determination review and re-approving.
            </p>
          )}
        </div>

        {form.officer_position_response && (
          <div className="md:col-span-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-800">The assessor's answer to your position of record</div>
            <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{form.officer_position_response}</p>
          </div>
        )}
        {form.verification_response && (
          <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">The assessor's answer to the reviewing attorney's finding</div>
            <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{form.verification_response}</p>
          </div>
        )}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-600 mb-1">Summary for the case file (why we reached this conclusion)</label>
          <textarea
            value={form.narrative_summary}
            onChange={(e) => update('narrative_summary', e.target.value)}
            disabled={!editable}
            placeholder="Brief explanation of how you reached this determination, for auditors and leadership."
            rows={4}
            className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
      </div>

      {saveError && (
        <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{saveError}</div>
      )}
      {editable && (
        <div className="flex gap-2">
          <button type="button" onClick={saveCurrent} disabled={saving} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save assessment'}
          </button>
          <button type="button" onClick={createNewVersion} disabled={creatingNew} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors">
            New version
          </button>
        </div>
      )}

      {/* Calibrate the assessor. Quiet, professional. A reviewer note that the
          assessor applies to future assessments of similar cases. */}
      {canEdit && (
        <details className="border-t border-slate-200 pt-4">
          <summary className="text-sm font-medium text-slate-600 cursor-pointer select-none hover:text-slate-800">
            Reviewer note to the assessor (optional)
          </summary>
          <p className="text-xs text-slate-500 mt-2 max-w-3xl">
            If the assessor mis-weighed something on this type of case, record a brief correction. Choose the part of the assessment it concerns so the assessor applies it precisely. It is logged with your name and applied to future assessments of similar incidents. It does not change this assessment, which remains yours to edit and approve.
          </p>
          <div className="mt-2 max-w-3xl">
            <label className="block text-xs font-medium text-slate-500 mb-1">This note is about</label>
            <select
              value={feedbackAbout}
              onChange={(e) => setFeedbackAbout(e.target.value)}
              className="w-full rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm text-slate-900"
            >
              {FEEDBACK_ABOUT.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={2}
            placeholder="e.g. When a forensic report shows data on the dark web, treat the recipient as malicious and acquisition as established."
            className="mt-2 w-full max-w-3xl rounded-xl border border-slate-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm text-slate-900"
          />
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <button type="button" onClick={sendFeedback} disabled={feedbackBusy || !feedbackText.trim()} className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 disabled:opacity-50">
              {feedbackBusy ? 'Saving…' : 'Record note'}
            </button>
            {feedbackMsg && <span className="text-xs text-slate-500">{feedbackMsg}</span>}
          </div>
        </details>
      )}
    </div>
  );
}
